import { Button, CircularProgress, ListItemIcon, ListItemButton, ListItemText } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import { sendbirdSelectors, useSendbirdStateContext } from '@sendbird/uikit-react';
import { Business_Profile_Enum, Company, Contract } from '@kheops/graphql';
import { currentContextAtom } from '../state';
import { DisplayPhoto } from '../common/common';
import SizedImage from '../common/components/SizedImage';
import FileUtils from '../common/utils/file.utils';
import { useJoinOrCreateChat } from '../hooks/useJoinOrCreateChat';
import { currentChannelAtom, newChannelSlideOpenAtom } from './state';

type ContactableCompany = Pick<Company, 'id' | 'tradeName'> & { photos: DisplayPhoto[] };
type ContactableContract = Pick<Contract, 'id' | 'channel_url'>
  & {
    buying_company: ContactableCompany;
    supplying_company: ContactableCompany;
  };

export interface ContactableCompanyRowProps {
  contract: ContactableContract;
}

export default function ContactableCompanyRow({ contract }: ContactableCompanyRowProps): React.JSX.Element {
  const globalStore = useSendbirdStateContext();
  const sdkInstance = sendbirdSelectors.getSdk(globalStore);
  const { t } = useTranslation(['common']);
  const { realm } = useAtomValue(currentContextAtom);
  const setCurrentChannel = useSetAtom(currentChannelAtom);
  const setNewChannelSlideOpen = useSetAtom(newChannelSlideOpenAtom);
  const company = realm === Business_Profile_Enum.Buyer ? contract.supplying_company : contract.buying_company;
  const [enableClick, setEnableClick] = useState(true);
  const { joinOrCreateChat, loading, channelUrl, joined } = useJoinOrCreateChat();

  const handleRowClick = (): void => {
    setEnableClick(false);
    joinOrCreateChat({
      id: contract.id,
      buyingCompanyId: contract.buying_company.id,
      supplyingCompanyId: contract.supplying_company.id,
      channel_url: contract.channel_url,
    })
  };

  useEffect((): void => {
    const updateCurrentChannel = async (newChannelUrl: string): Promise<void> => {
      const channelInstance = await sdkInstance?.groupChannel.getChannel(newChannelUrl);

      setCurrentChannel(channelInstance);
      setNewChannelSlideOpen(false);
    };

    if (channelUrl && !joined) {
      updateCurrentChannel(channelUrl);
    } else if (channelUrl && joined) {
      updateCurrentChannel(channelUrl);
      setNewChannelSlideOpen(false);
    }
  }, [channelUrl, joined]);

  return (
    <ListItemButton
      component={Button}
      onClick={handleRowClick}
      disabled={!enableClick}
      loading={loading}
      loadingIndicator={<CircularProgress size={20} color="primary" />}
    >
      <ListItemIcon>
        <SizedImage
          src={
            FileUtils.BuildCompanyImageUrl({
              photo: company.photos[0]?.photo,
              size: { height: 48, width: 48 },
              type: realm === Business_Profile_Enum.Buyer ? Business_Profile_Enum.Supplier : Business_Profile_Enum.Buyer,
            })
          }
          borderRadius={4}
          alt={t('common:company')}
          height={48}
          width={48}
        />
      </ListItemIcon>
      {!loading && (
        <ListItemText>
          {company.tradeName}
        </ListItemText>
      )}
    </ListItemButton>
  );
}
